/**
 * Reset Password Page
 * Allows users to set a new password using a reset token.
 *
 * Wired to the auth service (mock or http via the service factory). There is no
 * standalone token-validation endpoint, so a present token simply enables the
 * form; the token is actually validated server-side at submit time, and an
 * invalid/expired token flips the page to the "invalid link" state.
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { KeyRound, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { getAuthService } from '@/services/auth'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)

  // A present token enables the form; absence is the only client-side reject.
  // (The token is validated server-side on submit.)
  useEffect(() => {
    setTokenValid(Boolean(token))
  }, [token])

  // Password validation
  const passwordRequirements = [
    { label: 'At least 8 characters', valid: password.length >= 8 },
    { label: 'Contains uppercase letter', valid: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter', valid: /[a-z]/.test(password) },
    { label: 'Contains number', valid: /\d/.test(password) },
  ]

  const isPasswordValid = passwordRequirements.every((req) => req.valid)
  const doPasswordsMatch = password === confirmPassword && password.length > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!token) {
      setTokenValid(false)
      return
    }

    if (!isPasswordValid) {
      setError('Please meet all password requirements')
      return
    }

    if (!doPasswordsMatch) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      await getAuthService().resetPassword(token, password)
      setIsSuccess(true)
      // Redirect to login after delay
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password'
      // An invalid/expired token is a dead end — surface the dedicated state.
      if (/invalid|expired/i.test(message)) {
        setTokenValid(false)
      } else {
        setError(message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Loading state while validating token
  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-white" />
          <p className="text-white mt-4">Validating reset link...</p>
        </div>
      </div>
    )
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center px-4 py-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
        </div>

        <div className="relative max-w-md w-full">
          <div className="card card-elevated p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mx-auto">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary">Invalid or Expired Link</h2>
            <p className="text-sm text-text-secondary">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <div className="pt-4 space-y-3">
              <Link to="/forgot-password" className="btn btn-primary w-full">
                Request New Link
              </Link>
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm text-text-secondary hover:text-primary-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
      </div>

      {/* Content Card */}
      <div className="relative max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-white/10 backdrop-blur-sm mb-4">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Reset Password</h1>
          <p className="text-primary-100">
            {isSuccess ? 'Password reset successful!' : 'Enter your new password below'}
          </p>
        </div>

        {/* Form Card */}
        <div className="card card-elevated p-8 space-y-6">
          {isSuccess ? (
            /* Success State */
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-2">Password Updated!</h2>
                <p className="text-sm text-text-secondary">
                  Your password has been successfully reset. You can now log in with your new
                  password.
                </p>
              </div>
              <div className="pt-4">
                <p className="text-xs text-text-tertiary mb-3">Redirecting to login...</p>
                <Link to="/login" className="btn btn-primary w-full">
                  Go to Login
                </Link>
              </div>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Alert */}
              {error && (
                <div className="card card-elevated p-4 border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                  </div>
                </div>
              )}

              {/* New Password Input */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="input input-bordered w-full pr-10"
                    disabled={isLoading}
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password Requirements */}
                <div className="mt-3 space-y-1">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${req.valid ? 'bg-green-500' : 'bg-gray-300'}`}
                      />
                      <span className={req.valid ? 'text-green-600' : 'text-text-tertiary'}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className={`input input-bordered w-full pr-10 ${
                      confirmPassword && !doPasswordsMatch ? 'input-error' : ''
                    }`}
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {confirmPassword && !doPasswordsMatch && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
                {confirmPassword && doPasswordsMatch && (
                  <p className="text-xs text-green-500 mt-1">Passwords match</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !isPasswordValid || !doPasswordsMatch}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="loading loading-spinner loading-sm" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    Reset Password
                  </>
                )}
              </button>
            </form>
          )}

          {/* Back to Login */}
          {!isSuccess && (
            <div className="pt-4 border-t border-surface-overlay">
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm text-text-secondary hover:text-primary-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-6 text-center text-sm text-primary-100">
          <p>Demo application • All data is mock</p>
        </div>
      </div>
    </div>
  )
}
